import 'reflect-metadata';
import * as React from 'react';
import { Container } from 'inversify';
import HttpService from 'src/core/services/http.service';
import { AppConfiguration, ConfigurationService } from 'src/core/services/configuration.service';
import { PreferencesService } from 'src/core/services/preferences.service';
import { UserItemStore, UserProfileItemStore, UserSharingDataSettingsStore, UserSummaryStore } from './stores/users-store';
import { RoleItemStore, RoleSummaryStore } from './stores/roles-store';
import { DefaultRoleItemStore, DefaultRoleSummaryStore } from './stores/default-roles-store';
import { PermissionItemStore, PermissionSummaryStore } from './stores/permissions-store';
import { GroupItemStore, GroupSummaryStore } from './stores/groups-store';
import { RoleAssignmentItemStore, RoleAssignmentSummaryStore } from './stores/role-assignments-store';
import {
  OrganizationSummaryStore,
  OrganizationItemStore,
  OrganizationDatabaseLogsStore,
  OrganizationDatabaseLogsResultStore,
  OrganizationImportStore,
  OrganizationBackupStore,
  OrganizationDatabaseInconsistencyLogStore,
  OrganizationDatabaseInconsistencyLogResultStore,
} from './stores/organizations-store';
import { AuditLogSummaryStore } from './stores/audit-logs-store';
import { ScopeSummaryStore } from './stores/scopes-store';
import PermissionService from 'src/common/permission-service';
import { LicenseLogSummaryStore, LicenseLogReportItemStore, StructmasterLicenseLogsStore } from './stores/licensing-logs-store';
import {
  LicenseCreationAttributesStore,
  LicenseDataItemStore,
  LicenseDataSummaryStore,
  LicenseSuggestionsStore,
  LicenseSummaryStore,
  ReportItemStore,
  StructmasterLicensesStore,
} from './stores/licensing-store';
import { UserItemStoreMock, UserSummaryStoreMock } from './mocks/stores/users-store';
import {
  LicenseCreationAttributesStoreMock,
  LicenseDataItemStoreMock,
  LicenseDataSummaryStoreMock,
  LicenseSuggestionsStoreMock,
  LicenseSummaryStoreMock,
  ReportItemStoreMock,
} from './mocks/stores/licensing-store';
import { LicenseLogReportItemStoreMock, LicenseLogSummaryStoreMock } from './mocks/stores/licensing-logs-store';
import { RoleItemStoreMock, RoleSummaryStoreMock } from './mocks/stores/roles-store';
import { PermissionItemStoreMock, PermissionSummaryStoreMock } from './mocks/stores/permissions-store';
import { GroupItemStoreMock, GroupSummaryStoreMock } from './mocks/stores/groups-store';
import { RoleAssignmentItemStoreMock, RoleAssignmentSummaryStoreMock } from './mocks/stores/role-assignments-store';
import {
  OrganizationBackupStoreMock,
  OrganizationDatabaseInconsistencyLogResultStoreMock,
  OrganizationDatabaseInconsistencyLogStoreMock,
  OrganizationDatabaseLogsResultStoreMock,
  OrganizationDatabaseLogsStoreMock,
  OrganizationImportStoreMock,
  OrganizationItemStoreMock,
  OrganizationSummaryStoreMock,
} from './mocks/stores/organizations-store';
import { DefaultRoleItemStoreMock, DefaultRoleSummaryStoreMock } from './mocks/stores/default-roles-store';
import { AuditLogSummaryStoreMock } from './mocks/stores/audit-logs-store';
import { ScopeSummaryStoreMock } from './mocks/stores/scopes-store';
import { ImportExportDevLogsStore } from './stores/dev-logs-store';
import { ImportExportDevLogsStoreMock } from './mocks/stores/dev-logs-store';
import { ScopeLogsStore } from './stores/dev-logs-store';
import { ScopeLogsStoreMock } from './mocks/stores/dev-logs-store';
import { OrganizationStatisticsStore } from './stores/statistics-store';
import { ClientItemStore, ClientSummaryStore, ClientUsageSummaryStore } from './stores/clients-store';
import { ToolUsageReportStore } from './stores/tools-store';
import { ScheduledEventItemStore, ScheduledEventSummaryStore } from './stores/scheduled-events-store';
import { NotificationDataStore, NotificationPreferencesStore } from './stores/notifications-store';
import { TutorialFunctionItemStore, TutorialFunctionSummaryStore, TutorialItemStore, TutorialSummaryStore } from './stores/video-tutorials-store';

// Initialize DI/IoC container
const container = new Container();

const AppConfig = React.createContext<AppConfiguration>({} as any);

function initialize(config?: any, useMock?: boolean) {
  let baseUri = process.env.PUBLIC_URL || config ? config.identityServerUrl : `${window.location.protocol}//${window.location.host}`;
  let licensesUri = `${config.serviceUrl}`;
  let exportImportUri = `${config.exportImportUrl}`;
  if (!container.isBound(HttpService)) {
    // Initialize services if container is not configured before
    container
      .bind(HttpService)
      .toSelf()
      .inSingletonScope()
      .onActivation((context: any, instance: any) => {
        instance.setup(baseUri);
        return instance;
      });
  }

  // Initialize services
  container
    .bind(ConfigurationService)
    .toSelf()
    .inSingletonScope()
    .onActivation((context, instance) => {
      instance.current();
      return instance;
    });

  container
    .bind(PreferencesService)
    .toSelf()
    .inSingletonScope()
    .onActivation((context, instance) => {
      instance.current();
      return instance;
    });

  if (useMock) {
    container.bind(UserSummaryStore).toDynamicValue(() => new UserSummaryStoreMock(config.identityServerUrl));
    container.bind(LicenseDataSummaryStore).toConstantValue(new LicenseDataSummaryStoreMock(licensesUri));
    container.bind(LicenseDataItemStore).toConstantValue(new LicenseDataItemStoreMock(licensesUri));
    container.bind(LicenseSummaryStore).toDynamicValue(() => new LicenseSummaryStoreMock(licensesUri));
    container.bind(LicenseSuggestionsStore).toConstantValue(new LicenseSuggestionsStoreMock(licensesUri));
    container.bind(LicenseCreationAttributesStore).toDynamicValue(() => new LicenseCreationAttributesStoreMock(licensesUri));
    container.bind(LicenseLogSummaryStore).toConstantValue(new LicenseLogSummaryStoreMock(licensesUri));
    container.bind(LicenseLogReportItemStore).toConstantValue(new LicenseLogReportItemStoreMock(licensesUri));
    container.bind(ReportItemStore).toConstantValue(new ReportItemStoreMock(licensesUri, []));
    container.bind(UserItemStore).toConstantValue(new UserItemStoreMock(baseUri));
    container.bind(RoleSummaryStore).toConstantValue(new RoleSummaryStoreMock(baseUri));
    container.bind(RoleItemStore).toConstantValue(new RoleItemStoreMock(baseUri));
    container.bind(PermissionSummaryStore).toConstantValue(new PermissionSummaryStoreMock(baseUri));
    container.bind(PermissionItemStore).toConstantValue(new PermissionItemStoreMock(baseUri));
    container.bind(GroupSummaryStore).toConstantValue(new GroupSummaryStoreMock(baseUri));
    container.bind(GroupItemStore).toConstantValue(new GroupItemStoreMock(baseUri));
    container.bind(RoleAssignmentSummaryStore).toConstantValue(new RoleAssignmentSummaryStoreMock(baseUri));
    container.bind(RoleAssignmentItemStore).toConstantValue(new RoleAssignmentItemStoreMock(baseUri));
    container.bind(OrganizationSummaryStore).toConstantValue(new OrganizationSummaryStoreMock(baseUri));
    container.bind(OrganizationItemStore).toConstantValue(new OrganizationItemStoreMock(baseUri));
    container.bind(ScopeSummaryStore).toConstantValue(new ScopeSummaryStoreMock(baseUri));
    container.bind(DefaultRoleSummaryStore).toConstantValue(new DefaultRoleSummaryStoreMock(baseUri));
    container.bind(AuditLogSummaryStore).toConstantValue(new AuditLogSummaryStoreMock(baseUri));
    container.bind(DefaultRoleItemStore).toConstantValue(new DefaultRoleItemStoreMock(baseUri));
    container.bind(PermissionService).toConstantValue(new PermissionService(baseUri, container));
    container.bind(OrganizationDatabaseLogsStore).toConstantValue(new OrganizationDatabaseLogsStoreMock(baseUri));
    container.bind(OrganizationDatabaseLogsResultStore).toConstantValue(new OrganizationDatabaseLogsResultStoreMock(baseUri));
    container.bind(OrganizationImportStore).toDynamicValue(() => new OrganizationImportStoreMock(exportImportUri));
    container.bind(ImportExportDevLogsStore).toConstantValue(new ImportExportDevLogsStoreMock(exportImportUri));
    container.bind(OrganizationBackupStore).toConstantValue(new OrganizationBackupStoreMock(exportImportUri));
    container.bind(ScopeLogsStore).toConstantValue(new ScopeLogsStoreMock(baseUri));
    container.bind(OrganizationDatabaseInconsistencyLogStore).toConstantValue(new OrganizationDatabaseInconsistencyLogStoreMock(baseUri));
    container.bind(OrganizationDatabaseInconsistencyLogResultStore).toConstantValue(new OrganizationDatabaseInconsistencyLogResultStoreMock(baseUri));
  } else {
    container.bind(TutorialItemStore).toConstantValue(new TutorialItemStore(baseUri));
    container.bind(TutorialSummaryStore).toConstantValue(new TutorialSummaryStore(baseUri));
    container.bind(TutorialFunctionItemStore).toConstantValue(new TutorialFunctionItemStore(baseUri));
    container.bind(TutorialFunctionSummaryStore).toConstantValue(new TutorialFunctionSummaryStore(baseUri));
    container.bind(UserSummaryStore).toDynamicValue(() => new UserSummaryStore(config.identityServerUrl));
    container.bind(LicenseDataSummaryStore).toConstantValue(new LicenseDataSummaryStore(licensesUri));
    container.bind(LicenseDataItemStore).toConstantValue(new LicenseDataItemStore(licensesUri));
    container.bind(LicenseSummaryStore).toDynamicValue(() => new LicenseSummaryStore(licensesUri));
    container.bind(LicenseSuggestionsStore).toConstantValue(new LicenseSuggestionsStore(licensesUri));
    container.bind(LicenseCreationAttributesStore).toDynamicValue(() => new LicenseCreationAttributesStore(licensesUri));
    container.bind(LicenseLogSummaryStore).toConstantValue(new LicenseLogSummaryStore(licensesUri));
    container.bind(LicenseLogReportItemStore).toConstantValue(new LicenseLogReportItemStore(licensesUri));
    container.bind(StructmasterLicenseLogsStore).toConstantValue(new StructmasterLicenseLogsStore(baseUri));
    container.bind(ReportItemStore).toConstantValue(new ReportItemStore(licensesUri));
    container.bind(UserItemStore).toConstantValue(new UserItemStore(baseUri));
    container.bind(UserProfileItemStore).toConstantValue(new UserProfileItemStore(baseUri));
    container.bind(RoleSummaryStore).toConstantValue(new RoleSummaryStore(baseUri));
    container.bind(RoleItemStore).toConstantValue(new RoleItemStore(baseUri));
    container.bind(PermissionSummaryStore).toConstantValue(new PermissionSummaryStore(baseUri));
    container.bind(PermissionItemStore).toConstantValue(new PermissionItemStore(baseUri));
    container.bind(GroupSummaryStore).toConstantValue(new GroupSummaryStore(baseUri));
    container.bind(GroupItemStore).toConstantValue(new GroupItemStore(baseUri));
    container.bind(RoleAssignmentSummaryStore).toConstantValue(new RoleAssignmentSummaryStore(baseUri));
    container.bind(RoleAssignmentItemStore).toConstantValue(new RoleAssignmentItemStore(baseUri));
    container.bind(OrganizationSummaryStore).toConstantValue(new OrganizationSummaryStore(baseUri));
    container.bind(OrganizationItemStore).toConstantValue(new OrganizationItemStore(baseUri));
    container.bind(ScopeSummaryStore).toConstantValue(new ScopeSummaryStore(baseUri));
    container.bind(DefaultRoleSummaryStore).toConstantValue(new DefaultRoleSummaryStore(baseUri));
    container.bind(AuditLogSummaryStore).toConstantValue(new AuditLogSummaryStore(baseUri));
    container.bind(DefaultRoleItemStore).toConstantValue(new DefaultRoleItemStore(baseUri));
    container.bind(PermissionService).toConstantValue(new PermissionService(baseUri, container));
    container.bind(OrganizationDatabaseLogsStore).toConstantValue(new OrganizationDatabaseLogsStore(baseUri));
    container.bind(OrganizationDatabaseLogsResultStore).toConstantValue(new OrganizationDatabaseLogsResultStore(baseUri));
    container.bind(OrganizationImportStore).toDynamicValue(() => new OrganizationImportStore(exportImportUri));
    container.bind(ImportExportDevLogsStore).toConstantValue(new ImportExportDevLogsStore(exportImportUri));
    container.bind(OrganizationBackupStore).toConstantValue(new OrganizationBackupStore(exportImportUri));
    container.bind(ScopeLogsStore).toConstantValue(new ScopeLogsStore(baseUri));
    container.bind(OrganizationDatabaseInconsistencyLogStore).toConstantValue(new OrganizationDatabaseInconsistencyLogStore(baseUri));
    container.bind(OrganizationDatabaseInconsistencyLogResultStore).toConstantValue(new OrganizationDatabaseInconsistencyLogResultStore(baseUri));
    container.bind(OrganizationStatisticsStore).toConstantValue(new OrganizationStatisticsStore(baseUri));
    container.bind(UserSharingDataSettingsStore).toConstantValue(new UserSharingDataSettingsStore(baseUri));
    container.bind(StructmasterLicensesStore).toConstantValue(new StructmasterLicensesStore(baseUri));
    container.bind(ClientItemStore).toConstantValue(new ClientItemStore(baseUri));
    container.bind(ClientSummaryStore).toConstantValue(new ClientSummaryStore(baseUri));
    container.bind(ToolUsageReportStore).toConstantValue(new ToolUsageReportStore(baseUri));
    container.bind(ClientUsageSummaryStore).toConstantValue(new ClientUsageSummaryStore(baseUri));
    container.bind(ScheduledEventItemStore).toConstantValue(new ScheduledEventItemStore(baseUri));
    container.bind(ScheduledEventSummaryStore).toConstantValue(new ScheduledEventSummaryStore(baseUri));
    container.bind(NotificationDataStore).toConstantValue(new NotificationDataStore(config.notificationsUrl));
    container.bind(NotificationPreferencesStore).toConstantValue(new NotificationPreferencesStore(config.notificationsUrl));
  }
}

export { container, initialize, AppConfig };
